import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Restores the Supabase session on mount and checks if the user is a
 * platform admin (exists in the `platform_admins` table).
 *
 * IMPORTANT: The onAuthStateChange callback must NOT be async and must NOT
 * await any Supabase operations. Doing so deadlocks the client — signInWithPassword
 * and other auth calls will never resolve. All async work is deferred via
 * setTimeout so the callback returns immediately.
 *
 * Initialization flow:
 *  1. INITIAL_SESSION fires → setInitialized(true)
 *     - If token is valid  → defer admin check to next tick
 *     - If token is expired → defer admin check to TOKEN_REFRESHED event
 *  2. Admin check completes → setAdminCheckPending(false) → UI unlocked
 *  3. 10 s fallback handles the case where INITIAL_SESSION / TOKEN_REFRESHED never fire
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const setSession = useAuthStore((s) => s.setSession)
  const setIsPlatformAdmin = useAuthStore((s) => s.setIsPlatformAdmin)
  const setInitialized = useAuthStore((s) => s.setInitialized)
  const setAdminCheckPending = useAuthStore((s) => s.setAdminCheckPending)
  const initialized = useAuthStore((s) => s.initialized)
  const adminCheckPending = useAuthStore((s) => s.adminCheckPending)

  async function checkPlatformAdmin(userId: string) {
    console.log('[auth] checkPlatformAdmin called for userId:', userId)
    try {
      const { data, error } = await supabase
        .from('platform_admins')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle()
      console.log('[auth] checkPlatformAdmin result — data:', data, 'error:', error)
      setIsPlatformAdmin(!!data)
    } catch (err) {
      console.error('[auth] checkPlatformAdmin threw:', err)
      setIsPlatformAdmin(false)
    }
  }

  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setInitialized(true)
      setAdminCheckPending(false)
    }, 10000)

    // Tracks whether the initial admin check (tied to app startup) has finished.
    // Used to defer the check from INITIAL_SESSION to TOKEN_REFRESHED when the
    // stored token is already expired.
    let adminCheckCompleted = false

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // ── Synchronous state update only ───────────────────────────────────────
      // Do NOT make this callback async. Do NOT await anything here.
      // Any Supabase query inside an async onAuthStateChange callback will
      // deadlock the client and cause auth operations to hang forever.
      // All async work is deferred with setTimeout so this callback returns fast.
      // ────────────────────────────────────────────────────────────────────────
      console.log('[auth] onAuthStateChange event:', event, 'session?.user?.id:', session?.user?.id)
      setSession(session)

      if (event === 'INITIAL_SESSION') {
        setInitialized(true)

        if (!session) {
          setIsPlatformAdmin(false)
          setAdminCheckPending(false)
          adminCheckCompleted = true
          clearTimeout(fallbackTimer)
          return
        }

        const now = Math.floor(Date.now() / 1000)
        const tokenValid = !session.expires_at || session.expires_at > now
        console.log('[auth] INITIAL_SESSION — expires_at:', session.expires_at, 'now:', now, 'tokenValid:', tokenValid)

        if (tokenValid) {
          // Defer to next tick — lets the Supabase client release its internal
          // lock before we make a PostgREST request with the session JWT.
          setTimeout(() => {
            checkPlatformAdmin(session.user.id).finally(() => {
              adminCheckCompleted = true
              setAdminCheckPending(false)
              clearTimeout(fallbackTimer)
            })
          }, 0)
        }
        // Token is expired — Supabase will fire TOKEN_REFRESHED shortly.
        // Admin check is deferred to that event to avoid querying with a stale JWT.

      } else if (event === 'TOKEN_REFRESHED') {
        if (!adminCheckCompleted && session?.user) {
          // Complete the pending startup admin check with a fresh token
          setTimeout(() => {
            checkPlatformAdmin(session.user.id).finally(() => {
              adminCheckCompleted = true
              setAdminCheckPending(false)
              clearTimeout(fallbackTimer)
            })
          }, 0)
        } else if (session?.user) {
          setTimeout(() => checkPlatformAdmin(session.user.id), 0)
        }

      } else if (event === 'SIGNED_IN') {
        if (session?.user) {
          setTimeout(() => checkPlatformAdmin(session.user.id), 0)
        }

      } else if (event === 'SIGNED_OUT') {
        setIsPlatformAdmin(false)
      }
    })

    return () => {
      clearTimeout(fallbackTimer)
      subscription.unsubscribe()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!initialized || adminCheckPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Memuat...</p>
      </div>
    )
  }

  return <>{children}</>
}
